/**
 * Equipment Loan Policy & Terms of Agreement (ATL & Faculty of Arts).
 * Source: official Faculty document; keep in sync if the policy is updated.
 */

export const EQUIPMENT_LOAN_POLICY_RAW = `The University of Hong Kong (HKU) – Faculty of Arts

Equipment Loan Policy & Terms of Agreement

1. Eligibility and Approval

Authorized Use: Equipment borrowing is restricted to current students and staff within the HKU Faculty of Arts.

Geographical Restriction: The use of the loaned equipment is strictly restricted to the Hong Kong Special Administrative Region.

Staff Endorsement (Students): For coursework, assignments, research, or other course‑related activities, students must obtain prior written endorsement (email or approved form) from the relevant class teacher or course instructor. This ensures the equipment is being used strictly for legitimate academic purposes.

Approval and Availability: All loan requests are subject to equipment availability and final approval by Faculty of Arts staff. The Faculty reserves the right to refuse or cancel any loan without obligation to provide reasons.

Non-Transferability: The loaned equipment is strictly for the registered borrower's use only. Loaning, transferring, or sub-leasing the equipment to any third party (including other HKU students) is strictly prohibited.

2. Borrower Responsibilities and Liability

Total Responsibility: From the moment of check-out until the equipment is formally returned and logged by Faculty staff, the borrower assumes full physical and financial responsibility for the equipment.

Definition of Equipment: "Equipment" includes the main device and all associated accessories, such as power adapters, cables, cases, memory cards, tripods, and peripherals.

Proper Care: The borrower must handle the equipment with the utmost care, ensuring it is stored securely and never left unattended in public spaces.

Risk of Loss or Damage: The borrower accepts full risk of loss, theft, or damage to the equipment, regardless of cause, except where such loss or damage is proven to result directly from Faculty negligence.

Data, Accounts and Privacy: The borrower is solely responsible for backing up their own files and logging out of all personal accounts (e.g., Google, Apple ID) before returning equipment. Where applicable, borrowers must ensure personal data are removed or devices reset prior to return. The Faculty of Arts bears no responsibility for data loss or privacy breaches and reserves the right to wipe devices without prior notice.

3. Damage, Loss, and Return Procedures

Inspection at Checkout: Borrowers must inspect equipment at the time of checkout. Any pre‑existing physical defects or operational issues must be reported immediately before leaving the counter. Any defects not reported at checkout will be deemed to have occurred during the loan period.

Damaged Equipment: In the event of severe damage or catastrophic failure, the borrower must return the broken device (including all shattered or detached parts) to the Faculty office. The physical return of the broken item is mandatory to serve as proof of damage and to process the incident.

Loss or Theft: Equipment reported as lost or stolen must be reported to the Faculty immediately. The borrower remains fully liable for all associated costs.

Financial Compensation: If equipment is damaged, lost, or stolen, the borrower must fully reimburse the Faculty of Arts for:

the exact repair cost; or

the full replacement cost based on the supplier's prevailing list price, as determined by the Faculty.

4. Organizational Protection & General Terms

Return Requirements: Equipment must be returned in person by the stipulated due date and time and formally checked in by Faculty staff.

Late Returns and Penalties: Late returns will incur a daily penalty fine as determined by the Faculty. Equipment overdue by more than five (5) working days will be automatically classified as lost, and the borrower will be billed for the full replacement cost.

Definition of Working Day: A "working day" refers to a weekday excluding holidays observed by the University of Hong Kong.

Compliance with IT Ethics: Borrowers must use equipment in accordance with the HKU Statement of Ethics on Information Technology (IT) Use. Equipment must not be used for illegal activities, software piracy, unauthorized downloading, or non‑academic purposes.

Force Majeure: In cases of University closure, severe weather, or other force majeure events, return deadlines and penalties may be adjusted at the sole discretion of the Faculty of Arts.

Right of Recall: The Faculty of Arts reserves the right to recall the loaned equipment at any time, for any reason, prior to the due date.

Policy Amendments: The Faculty reserves the right to amend, update, or modify these terms and conditions at any time to protect the interests of the University.

5. Acknowledgement

By borrowing Faculty of Arts equipment, the borrower confirms that they have read, understood, and agreed to comply with this Equipment Loan Policy & Terms of Agreement and all applicable University regulations.`;

export function splitPolicyBlocks(raw = EQUIPMENT_LOAN_POLICY_RAW) {
  return raw.trim().split(/\n\n+/);
}

export function htmlEscape(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Single-line numbered section titles only (e.g. "1. Eligibility and Approval"). */
export function isNumberedSectionHeading(block) {
  const t = block.trim();
  return /^[0-9]+\.\s+[^/\n]{3,120}$/.test(t) && !t.includes(':');
}

/** HTML block for user emails (pending / confirmed). policyPageUrl must be absolute. */
export function equipmentLoanPolicyEmailAppendHtml(policyPageUrl) {
  const blocks = splitPolicyBlocks();
  const esc = htmlEscape;
  const inner = blocks
    .map((b) => {
      const t = b.trim();
      if (isNumberedSectionHeading(t)) {
        return `<h4 style="margin:18px 0 8px 0;color:#92400e;font-size:14px;border-bottom:1px solid #fcd34d;padding-bottom:4px;">${esc(t)}</h4>`;
      }
      return `<p style="margin:0 0 10px 0;font-size:12px;line-height:1.55;color:#374151;">${esc(t).replace(/\n/g, '<br/>')}</p>`;
    })
    .join('');
  const url = esc(policyPageUrl);
  return `
<div style="margin-top:28px;padding-top:20px;border-top:2px solid #e5e7eb;">
  <h3 style="color:#1f2937;margin:0 0 10px 0;font-size:16px;">Equipment Loan Policy &amp; Terms of Agreement</h3>
  <p style="font-size:13px;color:#4b5563;margin:0 0 14px 0;">The following terms apply to Faculty of Arts equipment loans. You can also <a href="${url}" style="color:#b45309;">read this policy on the website</a>.</p>
  <div style="font-size:12px;">${inner}</div>
</div>`;
}

export function equipmentLoanPolicyEmailAppendPlainText(policyPageUrl) {
  return (
    `\n\n---\nEquipment Loan Policy & Terms of Agreement\nWebsite: ${policyPageUrl}\n\n` +
    EQUIPMENT_LOAN_POLICY_RAW.trim()
  );
}
